<div *ngIf="isLoading()" class="flex flex-col items-center justify-center py-20 gap-4">
  <i class="pi pi-spinner pi-spin text-4xl text-primary"></i>
  <p class="text-sm font-bold text-slate-500 tracking-tight">{{ 'common_loading' | translate }}...</p>
</div>

<form *ngIf="!isLoading()" [formGroup]="clinicForm" (ngSubmit)="onSubmit()"
  class="flex flex-col animate-fadeIn w-full">

  <div class="p-6">
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-x-20 gap-y-12">

    <!-- Left Column: Clinic Information -->
    <div class="flex flex-col gap-8">
      <div class="flex flex-col gap-1">
        <h3 class="text-xl font-bold text-slate-700 tracking-tight">{{ 'clinic_tab_title' | translate }}</h3>
        <p class="text-sm text-slate-400 font-medium">{{ 'clinic_tab_subtitle' | translate }}</p>
      </div>

      <!-- Logo Upload Section -->
      <div class="flex flex-col gap-3">
        <label class="text-sm font-semibold text-slate-500">{{ 'clinic_field_logo' | translate }}</label>
        <div class="flex items-center gap-6">
          <div class="relative group">
            <div
              class="w-32 h-32 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden transition-all group-hover:border-primary/50">
              <img *ngIf="logoPreview()" [src]="logoPreview()" class="w-full h-full object-cover">
              <div *ngIf="!logoPreview()" class="flex flex-col items-center gap-2 text-slate-400">
                <i class="pi pi-image text-3xl"></i>
              </div>
            </div>

            <button *ngIf="logoPreview() && canEdit" type="button" (click)="removeLogo()"
              class="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-rose-500 text-white shadow-lg flex items-center justify-center hover:bg-rose-600 transition-colors">
              <i class="pi pi-times text-[10px]"></i>
            </button>
          </div>

          <div class="flex flex-col gap-2">
            <div class="flex items-center gap-2" *hasPermission="'Configurações:edit'">
              <label class="cursor-pointer">
                <span
                  class="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 text-primary text-sm font-bold hover:bg-primary/20 transition-all">
                  <i class="pi pi-upload"></i>
                  {{ 'clinic_button_upload_logo' | translate }}
                </span>
                <input type="file" (change)="handleLogoSelect($event)" accept=".jpg,.jpeg,.png" class="hidden">
              </label>
            </div>
            <p class="text-[11px] text-slate-400 font-medium max-w-[200px]">
              {{ 'clinic_logo_hint' | translate }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-5">
        <!-- Firm -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-semibold text-slate-500">
            {{ 'clinic_field_firm' | translate }}<span class="text-rose-500 ml-0.5">*</span>
          </label>
          <input type="text" pInputText formControlName="corporate_reason"
            class="!w-full !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]">
        </div>

        <!-- Headquarters -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-semibold text-slate-500">
            {{ 'clinic_field_headquarters' | translate }}<span class="text-rose-500 ml-0.5">*</span>
          </label>
          <input type="text" pInputText formControlName="address_branches"
            class="!w-full !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]">
        </div>

        <!-- City -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-semibold text-slate-500">
            {{ 'clinic_field_city_establishment' | translate }}<span class="text-rose-500 ml-0.5">*</span>
          </label>
          <input type="text" pInputText formControlName="city"
            class="!w-full !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]">
        </div>

        <!-- Address -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-semibold text-slate-500">
            {{ 'clinic_field_address_establishment' | translate }}<span class="text-rose-500 ml-0.5">*</span>
          </label>
          <input type="text" pInputText formControlName="establishment_address"
            class="!w-full !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]">
        </div>

        <!-- Country -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-semibold text-slate-500">
            {{ 'clinic_field_country_establishment' | translate }}<span class="text-rose-500 ml-0.5">*</span>
          </label>
          <p-select formControlName="country"
            [options]="[{label: 'Portugal', value: 'PT'}, {label: 'Brasil', value: 'BR'}]" optionLabel="label"
            optionValue="value"
            styleClass="!w-full !rounded-xl !border-slate-200 !text-sm !h-[48px] !flex !items-center"></p-select>
        </div>

        <!-- Postal Code -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-semibold text-slate-500">
            {{ 'clinic_field_postal_code_establishment' | translate }}<span class="text-rose-500 ml-0.5">*</span>
          </label>
          <p-inputMask formControlName="postal_code" mask="9999-999"
            styleClass="!w-full !rounded-xl !border-slate-200 !text-sm !py-3 focus:!border-primary transition-all shadow-none h-[48px]"
            inputStyleClass="!w-full !rounded-xl !border-0 !px-4 !py-0 !text-sm shadow-none bg-transparent">
          </p-inputMask>
        </div>

        <!-- Hours Range -->
        <div class="grid grid-cols-2 gap-8">
          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-slate-500">
              {{ 'clinic_field_start_time' | translate }}<span class="text-rose-500 ml-0.5">*</span>
            </label>
            <p-select formControlName="opening_time" [options]="hours"
              styleClass="!w-full !rounded-xl !border-slate-200 !text-sm !h-[48px] !flex !items-center"
              dropdownIcon="pi pi-clock"></p-select>
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-slate-500">
              {{ 'clinic_field_end_time' | translate }}<span class="text-rose-500 ml-0.5">*</span>
            </label>
            <p-select formControlName="closing_time" [options]="hours"
              styleClass="!w-full !rounded-xl !border-slate-200 !text-sm !h-[48px] !flex !items-center"
              dropdownIcon="pi pi-clock"></p-select>
          </div>
        </div>

        <!-- Patient identifier preference -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-semibold text-slate-500">
            {{ 'clinic_field_patient_identifier' | translate }}
          </label>
          <p-select formControlName="patient_identifier_type" [options]="patientIdentifierOptions"
            optionValue="value"
            styleClass="!w-full !rounded-xl !border-slate-200 !text-sm !h-[48px] !flex !items-center">
            <ng-template let-opt pTemplate="selectedItem">{{ opt.label | translate }}</ng-template>
            <ng-template let-opt pTemplate="item">{{ opt.label | translate }}</ng-template>
          </p-select>
          <span class="text-xs text-slate-400 font-medium">
            {{ 'clinic_field_patient_identifier_hint' | translate }}
          </span>
        </div>

        <!-- Tooth numbering notation (display-only; DB always stores FDI) -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-semibold text-slate-500">
            {{ 'clinic_field_tooth_notation' | translate }}
          </label>
          <div class="flex items-center gap-6 h-12">
            <div *ngFor="let opt of toothNotationOptions" class="flex items-center gap-2">
              <p-radioButton
                name="tooth_notation"
                [value]="opt.value"
                formControlName="tooth_notation"
                [inputId]="'toothNotation_' + opt.value">
              </p-radioButton>
              <label [for]="'toothNotation_' + opt.value" class="text-sm text-slate-600 cursor-pointer select-none">
                {{ opt.label | translate }}
              </label>
            </div>
          </div>
          <span class="text-xs text-slate-400 font-medium">
            {{ 'clinic_field_tooth_notation_hint' | translate }}
          </span>
        </div>

        <!-- Clinic timezone -->
        <div class="flex flex-col gap-2">
          <label class="text-sm font-semibold text-slate-500">
            {{ 'clinic_field_timezone' | translate }}
          </label>
          <p-select formControlName="timezone" [options]="timezoneOptions"
            optionValue="value"
            [filter]="true"
            filterBy="label"
            styleClass="!w-full !rounded-xl !border-slate-200 !text-sm !h-[48px] !flex !items-center">
            <ng-template #selectedItem let-opt>{{ opt.value === null ? (opt.label | translate) : opt.label }}</ng-template>
            <ng-template #item let-opt>{{ opt.value === null ? (opt.label | translate) : opt.label }}</ng-template>
          </p-select>
          <span class="text-xs text-slate-400 font-medium">
            {{ 'clinic_field_timezone_hint' | translate }}
          </span>
        </div>

        <!-- CRM sidebar link. Only rendered for clinics that actually hold a
             CRM subscription — a switch for a product you do not have is noise.
             It hides an available link; it never grants access. -->
        <div *ngIf="hasCrmAccess()"
          class="flex items-center justify-between gap-4 pt-4 border-t border-slate-100">
          <div class="flex flex-col gap-1 max-w-md">
            <label class="text-sm font-semibold text-slate-500">
              {{ 'clinic_field_show_crm_link' | translate }}
            </label>
            <span class="text-xs text-slate-400 font-medium">
              {{ 'clinic_field_show_crm_link_hint' | translate }}
            </span>
          </div>
          <p-toggleswitch formControlName="show_crm_link" [disabled]="!canEdit"></p-toggleswitch>
        </div>
      </div>
    </div>

    <!-- Right Column: System Integrations -->
    <div class="flex flex-col gap-14">
      <!-- GESFaturação -->
      <div class="flex flex-col gap-8">
        <div class="flex flex-col gap-1">
          <h3 class="text-xl font-bold text-slate-700 tracking-tight">{{ 'clinic_gesfaturacao_title' | translate }}</h3>
          <p class="text-sm text-slate-400 font-medium">{{ 'clinic_gesfaturacao_subtitle' | translate }}</p>
        </div>

        <div class="flex flex-col gap-5">
          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-slate-500">{{ 'clinic_field_invoice_series' | translate }}</label>
            <input type="text" pInputText formControlName="serieFatura"
              class="!w-full !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]">
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-slate-500">{{ 'clinic_field_nif' | translate }}</label>
            <input type="text" pInputText formControlName="nif"
              class="!w-full !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]">
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-slate-500">{{ 'clinic_field_user' | translate }}</label>
            <input type="text" pInputText formControlName="username"
              class="!w-full !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]">
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-slate-500">{{ 'clinic_field_password' | translate }}</label>
            <p-password formControlName="password" [toggleMask]="true" [feedback]="false" styleClass="w-full"
              inputStyleClass="!w-full !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]"></p-password>
          </div>

          <!-- Tax region: which GESFaturação VAT rates the tax pickers offer.
               Saved to this BRANCH (branches_tb.tax_region) with the rest of the
               form; none selected until a region is chosen = every rate. -->
          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-slate-500">{{ 'clinic_field_tax_region' | translate }}</label>
            <div class="flex flex-wrap items-center gap-x-6 gap-y-3 min-h-12">
              <div *ngFor="let opt of taxRegionOptions" class="flex items-center gap-2">
                <p-radioButton
                  name="tax_region"
                  [value]="opt.value"
                  formControlName="tax_region"
                  [inputId]="'taxRegion_' + opt.value">
                </p-radioButton>
                <label [for]="'taxRegion_' + opt.value" class="text-sm text-slate-600 cursor-pointer select-none">
                  {{ opt.label | translate }}
                </label>
              </div>
            </div>
            <span class="text-xs text-slate-400 font-medium">
              {{ 'clinic_field_tax_region_hint' | translate }}
            </span>
          </div>
        </div>
      </div>

      <!-- SIBS -->
      <div class="flex flex-col gap-8 pt-4">
        <div class="flex flex-col gap-1">
          <h3 class="text-xl font-bold text-slate-700 tracking-tight">{{ 'clinic_sibs_title' | translate }}</h3>
          <p class="text-sm text-slate-400 font-medium">{{ 'clinic_sibs_subtitle' | translate }}</p>
        </div>

        <div class="flex flex-col gap-5">
          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-slate-500">{{ 'clinic_field_x_ibm_client_id' | translate }}</label>
            <input type="text" pInputText formControlName="X-IBM-Client-Id-SIBS"
              [placeholder]="'clinic_field_x_ibm_client_id_placeholder' | translate"
              class="!w-full !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]">
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-slate-500">{{ 'clinic_field_auth_key' | translate }}</label>
            <input type="text" pInputText formControlName="authkey-SIBS"
              [placeholder]="'clinic_field_auth_key_placeholder' | translate"
              class="!w-full !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]">
          </div>
        </div>
      </div>

      <!-- Diagnocat -->
      <div *ngIf="authService.clinicType === 'dental'" class="flex flex-col gap-8 pt-4">
        <div class="flex flex-col gap-1">
          <h3 class="text-xl font-bold text-slate-700 tracking-tight">{{ 'clinic_diagnocat_title' | translate }}</h3>
          <p class="text-sm text-slate-400 font-medium">{{ 'clinic_diagnocat_subtitle' | translate }}</p>
        </div>

        <div class="flex flex-col gap-5">
          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-slate-500">{{ 'clinic_field_diagnocat_user' | translate }}</label>
            <input type="text" pInputText formControlName="diagnocat_user"
              class="!w-full !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]">
          </div>
          <div class="flex flex-col gap-2">
            <label class="text-sm font-semibold text-slate-500">{{ 'clinic_field_diagnocat_password' | translate
              }}</label>
            <p-password formControlName="diagnocat_pass" [toggleMask]="true" [feedback]="false" styleClass="w-full"
              inputStyleClass="!w-full !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]"></p-password>
          </div>
        </div>
      </div>

      <!-- Dio (key is write-only: set/replace, never displayed) -->
      <div *ngIf="authService.clinicType === 'dental'" class="flex flex-col gap-8 pt-4">
        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-3">
            <img src="assets/images/logo_dio_icon.png" class="h-5 w-auto" alt="Dio" />
            <h3 class="text-xl font-bold text-slate-700 tracking-tight">{{ 'clinic_dio_title' | translate }}</h3>
            <span *ngIf="dioConfigured"
              class="px-2 py-0.5 rounded-lg bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase tracking-wide">
              {{ 'active' | translate }}
            </span>
          </div>
          <p class="text-sm text-slate-400 font-medium">{{ 'clinic_dio_subtitle' | translate }}</p>
        </div>

        <div class="flex flex-col gap-2">
          <label class="text-sm font-semibold text-slate-500">{{ 'clinic_field_dio_api_key' | translate }}</label>
          <div class="flex gap-3">
            <input type="password" pInputText #dioKey autocomplete="off"
              [placeholder]="'clinic_dio_key_placeholder' | translate"
              class="!w-full !rounded-xl !border-slate-200 !px-4 !py-3 !text-sm focus:!border-primary transition-all shadow-none h-[48px]">
            <button type="button" [disabled]="dioKeySaving || !canEdit"
              (click)="saveDioKey(dioKey.value); dioKey.value = ''"
              class="px-5 rounded-xl bg-primary text-white text-sm font-bold hover:opacity-90 transition-all disabled:opacity-50 h-[48px] shrink-0">
              {{ 'save' | translate }}
            </button>
          </div>

          <!-- Which Dio clinic this branch points at. The identifier is Dio's,
               so it is read back from them rather than typed. Shown unlinked too:
               a branch whose discovery failed after saving the token would
               otherwise have no way back to it except re-typing the token. -->
          <div *ngIf="!dioClinics.length"
            class="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <ng-container *ngIf="dioClinicUuid; else dioNoClinic">
              <i class="pi text-[11px]"
                [ngClass]="dioClinicUnverified ? 'pi-exclamation-triangle text-amber-500' : 'pi-check-circle text-emerald-500'"></i>
              <span>{{ 'clinic_dio_linked_to' | translate }}</span>
              <!-- Name first, because that is what answers "which clinic";
                   the identifier stays visible, since it is the string the
                   provider's own URLs and their support use. -->
              <span *ngIf="dioClinicName" class="text-slate-700">{{ dioClinicName }}</span>
              <code class="px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-mono text-[11px]">{{ dioClinicUuid }}</code>
              <span *ngIf="dioClinicUnverified" class="text-amber-600 font-bold">
                {{ 'clinic_dio_clinic_unverified' | translate }}
              </span>
            </ng-container>
            <ng-template #dioNoClinic>
              <i class="pi pi-info-circle text-slate-400 text-[11px]"></i>
              <span>{{ 'clinic_dio_no_clinic' | translate }}</span>
            </ng-template>
            <!-- Asks the provider what this token can see: it confirms the
                 current link, and offers a choice only when there is one. -->
            <button type="button" *ngIf="canEdit" [disabled]="dioLinking"
              (click)="discoverDioClinics()"
              class="text-primary hover:underline disabled:opacity-50">
              {{ (dioClinicUuid ? 'clinic_dio_change_clinic' : 'clinic_dio_find_clinics') | translate }}
            </button>
          </div>

          <!-- Only shown when the token sees more than one clinic; a single one
               is linked automatically, since there is nothing to choose. -->
          <div *ngIf="dioClinics.length > 1" class="flex flex-col gap-2 pt-1">
            <label class="text-sm font-semibold text-slate-500">{{ 'clinic_dio_choose_clinic' | translate }}</label>
            <div class="flex gap-3">
              <!-- Each option carries its own [selected] so the rendered list
                   matches dioClinicChoice, which is preset to the currently
                   linked clinic: without it the box can read "choose a clinic"
                   while Link would re-link the old one. -->
              <select (change)="dioClinicChoice = $any($event.target).value"
                class="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:border-primary transition-all h-[48px] bg-white">
                <option value="" [selected]="!dioClinicChoice" disabled>{{ 'clinic_dio_choose_clinic' | translate }}</option>
                <option *ngFor="let c of dioClinics" [value]="c.uuid" [selected]="c.uuid === dioClinicChoice">{{ c.name || c.uuid }}</option>
              </select>
              <button type="button" [disabled]="dioLinking || !dioClinicChoice || !canEdit"
                (click)="linkDioClinic(dioClinicChoice)"
                class="px-5 rounded-xl bg-primary text-white text-sm font-bold hover:opacity-90 transition-all disabled:opacity-50 h-[48px] shrink-0">
                {{ 'clinic_dio_link' | translate }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Patient Self-Registration QR -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-slate-100">
        <!-- Standard Registration -->
        <div class="flex flex-col gap-6">
          <div class="flex flex-col gap-1">
            <h3 class="text-xl font-bold text-slate-700 tracking-tight">{{ 'clinic_self_reg_title' | translate }}</h3>
            <p class="text-sm text-slate-400 font-medium">{{ 'clinic_self_reg_subtitle' | translate }}</p>
          </div>

          <div class="bg-white rounded-[2rem] border border-slate-100 p-8 flex flex-col items-center gap-6 shadow-sm">
            <div class="w-48 h-48 bg-slate-50 rounded-3xl flex items-center justify-center border-2 border-dashed border-slate-200 overflow-hidden">
              <img [src]="qrCodeUrl()" class="w-full h-full object-contain p-4" *ngIf="qrCodeUrl()" alt="QR Code">
              <div *ngIf="!qrCodeUrl()" class="flex flex-col items-center gap-2 text-slate-300">
                <i class="pi pi-qrcode text-4xl"></i>
              </div>
            </div>
            
            <div class="flex flex-col items-center gap-2 text-center w-full">
              <p class="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">{{ 'clinic_self_reg_link' | translate }}</p>
              <div class="flex items-center gap-2 px-4 py-3 bg-slate-50 rounded-2xl border border-slate-100 w-full">
                <span class="text-xs font-bold text-slate-600 truncate flex-1">{{ registrationLink() }}</span>
                <button type="button" (click)="copyLink()" class="w-10 h-10 rounded-xl hover:bg-slate-200 flex items-center justify-center transition-all bg-white shadow-sm border border-slate-100">
                  <i class="pi pi-copy text-sm text-primary"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Registration -->
        <div class="flex flex-col gap-6">
          <div class="flex flex-col gap-1">
            <h3 class="text-xl font-bold text-slate-700 tracking-tight">{{ 'clinic_quick_reg_title' | translate }}</h3>
            <p class="text-sm text-slate-400 font-medium">{{ 'clinic_quick_reg_subtitle' | translate }}</p>
          </div>

          <div class="bg-white rounded-[2rem] border border-slate-100 p-8 flex flex-col items-center gap-6 shadow-sm">
            <div class="w-48 h-48 bg-slate-50 rounded-3xl flex items-center justify-center border-2 border-dashed border-slate-200 overflow-hidden">
              <img [src]="quickQrCodeUrl()" class="w-full h-full object-contain p-4" *ngIf="quickQrCodeUrl()" alt="QR Code">
              <div *ngIf="!quickQrCodeUrl()" class="flex flex-col items-center gap-2 text-slate-300">
                <i class="pi pi-qrcode text-4xl"></i>
              </div>
            </div>
            
            <div class="flex flex-col items-center gap-2 text-center w-full">
              <p class="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">{{ 'clinic_quick_reg_link' | translate }}</p>
              <div class="flex items-center gap-2 px-4 py-3 bg-slate-50 rounded-2xl border border-slate-100 w-full">
                <span class="text-xs font-bold text-slate-600 truncate flex-1">{{ quickRegistrationLink() }}</span>
                <button type="button" (click)="copyQuickLink()" class="w-10 h-10 rounded-xl hover:bg-slate-200 flex items-center justify-center transition-all bg-white shadow-sm border border-slate-100">
                  <i class="pi pi-copy text-sm text-primary"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  </div>

  <!-- Footer — sticks to the bottom of the viewport while the page scrolls,
       then scrolls away normally once the end of the form is reached. -->
  <div class="sticky bottom-0 z-10 flex justify-end items-center px-6 py-4 border-t border-slate-100 bg-white/95 backdrop-blur-sm shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
    <app-button *hasPermission="'Configurações:edit'" [label]="'common_save' | translate" type="submit" [loading]="isSaving()"
      [disabled]="clinicForm.invalid || isSaving()" styleClass="min-w-[180px]"></app-button>
  </div>
</form>